import axios from 'axios';
import Cookies from 'js-cookie';
import { CONFIG } from '@/config/Config';
import { addSessionId } from '@/core/utils/httpHeaders';

interface RawConversationPage {
  id?: string | number;
  title?: string;
  username?: string;
  channel_name?: string;
  channel?: string;
  page_active?: boolean | string | number;
  linked_universes?: (string | number)[];
}

interface ConversationPagesResponsePayload {
  message?: unknown[];
}

export interface ConversationPageAccountItem {
  id: string;
  title: string;
  username?: string;
  channel: string;
  linkedUniverses?: (string | number)[];
}

const normalizeChannelName = (raw: string | undefined): string =>
  String(raw ?? '')
    .trim()
    .toLowerCase();

const isPageActive = (raw: RawConversationPage): boolean => {
  const value = raw.page_active;

  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.trim().toLowerCase() === 'true';
  if (typeof value === 'number') return value === 1;

  return false;
};

const getConversationPagesSource = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) return payload;

  if (payload && typeof payload === 'object') {
    const objectPayload = payload as ConversationPagesResponsePayload;
    if (Array.isArray(objectPayload.message)) return objectPayload.message;
  }

  return [];
};

export async function fetchConversationPagesByAccount(): Promise<ConversationPageAccountItem[]> {
  const accountHeader = CONFIG.cookieOrParam(CONFIG.AUTH.ACC_ID) ?? undefined;
  const universeHeader = CONFIG.cookieOrParam(CONFIG.AUTH.UNI_ID) ?? undefined;
  const accountId = accountHeader?.split('|')[0];

  if (!accountId) {
    console.warn('accId is undefined, skipping fetchConversationPagesByAccount');
    return [];
  }

  const url = `${CONFIG.getGatewayUrl()}proprietary-page/${CONFIG.getVersion()}/listing/public/pages/${accountId}/all`;

  const headers = addSessionId({
    accept: 'application/json',
  });

  if (accountHeader) {
    headers['stilingue-account-id'] = accountHeader;
  }

  if (universeHeader) {
    headers['stilingue-universe-id'] = universeHeader;
  }

  const sessionToken = Cookies.get(CONFIG.AUTH.SSID);
  if (sessionToken) {
    headers.Authorization = `Bearer ${sessionToken}`;
  }

  const response = await axios.get(url, { headers });
  const rawItems = getConversationPagesSource(response.data);

  const mapped = rawItems
    .map((raw) => {
      if (!raw || typeof raw !== 'object') return null;

      const page = raw as RawConversationPage;
      if (!isPageActive(page)) return null;

      const channel = normalizeChannelName(page.channel_name ?? page.channel);
      if (channel === 'facebook_instagram') return null;

      const id = String(page.id ?? '').trim();
      const title = String(page.title ?? page.username ?? '').trim();
      const username = String(page.username ?? '').trim() || undefined;

      if (!id || !title || !channel) return null;

      return {
        id,
        title,
        username,
        channel,
        linkedUniverses: page.linked_universes,
      } as ConversationPageAccountItem;
    })
    .filter(Boolean) as ConversationPageAccountItem[];

  const deduped = new Map<string, ConversationPageAccountItem>();
  mapped.forEach((item) => {
    const key = `${item.channel}::${item.id}`;
    if (!deduped.has(key)) {
      deduped.set(key, item);
    }
  });

  return Array.from(deduped.values());
}
